import { Fragment, type ReactNode } from 'react';

/**
 * A contract, rendered as a document rather than as its source.
 *
 * ## Why this exists
 *
 * The body was shown in a `<pre>`, and the comment there argued the case: what
 * is stored is what the signature is a claim about, so putting a renderer
 * between the two risks the customer agreeing to something other than what was
 * kept. The argument is sound and the conclusion was wrong — a customer being
 * asked to sign was reading `# Internet Service Agreement` and `**KES 1,499**`,
 * hashes and asterisks and all. That does not look like a contract; it looks
 * like something half-built, which is a poor thing to feel while signing.
 *
 * The resolution is to render *faithfully*. Every character of text in the
 * source appears in the output; the only thing that changes is that a heading
 * looks like a heading. Nothing is reordered, nothing is summarised, nothing is
 * hidden. The stored text remains the thing signed, and it is still available
 * verbatim in the PDF and through the API.
 *
 * ## Why it is written here rather than pulled from npm
 *
 * A general markdown library would bring HTML passthrough with it, and HTML in
 * a document somebody is about to sign is an injection surface pointed at the
 * least technical person who touches this system. This handles the six things
 * the contract templates actually use — headings, bold, tables, lists, rules
 * and paragraphs — and treats everything else as literal text. There is no path
 * from the source to raw HTML, because none is written.
 */

/**
 * `**bold**` and open gaps inside a line of text. Everything else is literal.
 *
 * A gap is a `{{key}}` that survived `fillLetter` because the caller asked for
 * it to be left open, and `slots` says what goes there — an input, normally,
 * so somebody fills the blank in the sentence itself rather than in a form
 * somewhere below it. A gap with no slot falls back to the rule it would
 * otherwise have printed, so a stray placeholder can never leak braces onto a
 * document somebody is reading.
 */
function inline(text: string, slots?: Record<string, ReactNode>): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));

    if (match[1] !== undefined) {
      /*
       * Recursed, because a gap is very often inside bold.
       *
       * The agreement says "**{{terminationNoticeDays}} days'** written
       * notice", and a `<strong>` that pushed its contents as a raw string
       * printed the braces on the page — the alternation matched the bold
       * first, so the placeholder inside it was never looked at. Every clause
       * that emphasised the figure it was asking for leaked, and only those.
       */
      out.push(<strong key={`b${match.index}`}>{inline(match[1], slots)}</strong>);
    } else {
      const key = match[2] as string;
      out.push(
        <Fragment key={`s${match.index}`}>{slots?.[key] ?? '__________'}</Fragment>,
      );
    }

    last = match.index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** A `| a | b |` row, split on pipes with the outer ones dropped. */
function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
/** `|---|---|` — the divider under a header row, which is never shown. */
const isTableRule = (line: string) => /^\s*\|[\s:|-]+\|\s*$/.test(line);

export function ContractDocument({
  body,
  className = '',
  slots,
}: {
  body: string;
  className?: string;
  /**
   * What to render in each open gap, keyed by placeholder name.
   *
   * Omitted everywhere except the screens where somebody is filling the
   * document in — reading, printing and hashing all go through the same
   * renderer with no slots and behave exactly as they did.
   */
  slots?: Record<string, ReactNode>;
}) {
  const lines = body.split('\n');
  const blocks: ReactNode[] = [];

  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(
      <p key={`p${blocks.length}`} className="my-2 leading-relaxed">
        {inline(paragraph.join(' '), slots)}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={`u${blocks.length}`} className="my-2 list-disc space-y-1 pl-5">
        {list.map((item, index) => (
          <li key={index}>{inline(item, slots)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  const flush = () => {
    flushParagraph();
    flushList();
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (trimmed === '') {
      flush();
      continue;
    }

    /* ── Tables ──────────────────────────────────────────────────────────
     * Consumed as a run, because a table is the one structure here whose
     * meaning is spread across several lines. The contract uses two-column
     * tables with an empty header — `| | |` — for its figures, so a header row
     * of entirely blank cells is dropped rather than rendered as an empty band.
     */
    if (isTableRow(line)) {
      flush();
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]!)) {
        if (!isTableRule(lines[i]!)) rows.push(cells(lines[i]!));
        i += 1;
      }
      i -= 1;

      const head = rows[0] ?? [];
      /*
       * The contract uses two-column tables with an empty header — `| | |` — for
       * its figures. A blank header row is dropped rather than drawn as an empty
       * band; either way the first row is the header, so the body is the rest.
       */
      const headerIsBlank = head.every((cell) => cell === '');
      const bodyRows = rows.slice(1);

      blocks.push(
        <div key={`t${blocks.length}`} className="my-3 overflow-x-auto">
          <table className="doc-table w-full border-collapse text-sm">
            {!headerIsBlank && (
              <thead>
                <tr className="border-b border-border text-left">
                  {head.map((cell, index) => (
                    <th key={index} className="py-1.5 pr-4 font-medium">
                      {inline(cell, slots)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border/50 last:border-0">
                  {row.map((cell, index) => (
                    <td key={index} className="py-1.5 pr-4 align-top">
                      {inline(cell, slots)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    /* ── Headings ─────────────────────────────────────────────────────── */
    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flush();
      const level = heading[1]!.length;
      const text = heading[2]!;
      const size =
        level === 1
          ? 'mt-1 mb-3 font-heading text-xl'
          : level === 2
            ? 'mt-5 mb-2 font-heading text-base'
            : 'mt-4 mb-1 font-heading text-sm';
      blocks.push(
        <p key={`h${blocks.length}`} className={size}>
          {inline(text, slots)}
        </p>,
      );
      continue;
    }

    /* ── A horizontal rule ────────────────────────────────────────────── */
    if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      flush();
      blocks.push(<hr key={`r${blocks.length}`} className="my-4 border-border" />);
      continue;
    }

    /* ── Bullets ──────────────────────────────────────────────────────── */
    const bullet = /^[-*]\s+(.*)$/.exec(trimmed);
    if (bullet) {
      flushParagraph();
      list.push(bullet[1]!);
      continue;
    }

    flushList();
    paragraph.push(trimmed);
  }

  flush();

  return <div className={className}>{blocks.map((block, i) => <Fragment key={i}>{block}</Fragment>)}</div>;
}
